import {
  ApplicationStep,
  CoApplicantAddressMatch,
  LifeInsuranceApplicationStep,
  LifeInsuranceResultsView,
} from 'embed/constants'
import { MultiProductState } from 'embed/reducers'
import { configApplicationFlowSelector, configFixedWidthSelector } from 'embed/selectors/config'
import { loanAreAllOffersEmptySelector, loanExclusivelySpecialOffersSelector } from 'embed/selectors/rates'
import {
  CreditCardStep,
  DepositsStep,
  LifeInsuranceStep,
  LoanFlow,
  LoanStep,
  MortgageStep,
  Product,
} from 'even-ts-static'
import {
  ConfigProviderState,
  LifeInsuranceUIState,
  SelectedApplicationStepState,
  SelectedStepState,
} from 'embed/reducers/ui'

export const uiStateSelector = (state: MultiProductState): ConfigProviderState => state.ui
export const uiHasCalledGeocoderSelector = (state: MultiProductState): boolean =>
  uiStateSelector(state).hasCalledGeocoder
export const uiSelectedProductSelector = (state: MultiProductState): Product | undefined =>
  uiStateSelector(state).selectedProduct
export const uiSelectedStepSelector = (state: MultiProductState): SelectedStepState =>
  uiStateSelector(state)?.selectedStep
export const uiSelectedStepHistorySelector = (state: MultiProductState, product: Product) =>
  uiStateSelector(state).selectedStepHistory[product]
export const uiSelectedApplicationStepSelector = (state: MultiProductState): SelectedApplicationStepState =>
  uiStateSelector(state).selectedApplicationStep
export const uiSelectedLoanStepSelector = (state: MultiProductState): LoanStep => uiSelectedStepSelector(state).loan
export const uiSelectedLoanStepIsResultsSelector = (state: MultiProductState): boolean =>
  uiSelectedLoanStepSelector(state) === LoanStep.Results
export const uiSelectedLoanStepIsResultsWithLoanOffersSelector = (state: MultiProductState): boolean =>
  uiSelectedLoanStepIsResultsSelector(state) &&
  !(loanExclusivelySpecialOffersSelector(state) || loanAreAllOffersEmptySelector(state))
export const uiSelectedCreditApplicationStepSelector = (state: MultiProductState): ApplicationStep | undefined =>
  uiSelectedApplicationStepSelector(state)?.credit_card
export const uiSelectedCreditStepSelector = (state: MultiProductState): CreditCardStep =>
  uiSelectedStepSelector(state).credit_card
export const uiSelectedLifeInsuranceStepSelector = (state: MultiProductState): LifeInsuranceStep =>
  uiSelectedStepSelector(state).life_insurance
export const uiSelectedMortgageStepSelector = (state: MultiProductState): MortgageStep =>
  uiSelectedStepSelector(state)[Product.Mortgage]
export const uiSelectedDepositsStepSelector = (state: MultiProductState): DepositsStep =>
  uiSelectedStepSelector(state)[Product.Deposits]
export const uiSelectedLifeInsuranceApplicationStepSelector = (
  state: MultiProductState,
): LifeInsuranceApplicationStep | undefined => uiSelectedApplicationStepSelector(state).life_insurance
export const uiShowCoApplicantAddressFieldsSelector = (state: MultiProductState): boolean =>
  state.application.loan?.fields?.coApplicantAddressMatch?.value === CoApplicantAddressMatch.DifferentFromApplicant
export const uiLifeInsuranceSelector = (state: MultiProductState): LifeInsuranceUIState =>
  uiStateSelector(state)[Product.LifeInsurance]
export const uiLifeInsuranceResultsViewSelector = (state: MultiProductState): LifeInsuranceResultsView =>
  uiLifeInsuranceSelector(state).resultsView
export const uiLifeInsuranceCoverageAmountSelector = (state: MultiProductState): number =>
  uiLifeInsuranceSelector(state).coverageAmount
export const uiLifeInsuranceCoverageDurationSelector = (state: MultiProductState): number =>
  uiLifeInsuranceSelector(state).coverageDuration
export const hybridOfferListSelector = (state: MultiProductState): boolean =>
  !!uiStateSelector(state)?.isCCHybridOfferListEnabled

export const uiPreviewIsInitializingSelector = (state: MultiProductState): boolean => state.ui.previewIsInitializing

export const uiViewportWidthSelector = (state: MultiProductState): number | undefined => state.ui.viewport.width
export const uiViewportHeightSelector = (state: MultiProductState): number | undefined => state.ui.viewport.height

export const useCompactListItemSelector = (state: MultiProductState): boolean => {
  const viewportWidth = uiViewportWidthSelector(state)
  const viewportHeight = uiViewportHeightSelector(state)
  return viewportWidth !== undefined && viewportHeight !== undefined
    ? viewportWidth <= 300 && viewportHeight <= 800
    : false
}

export const isStepByStepSelector = (state: MultiProductState): boolean =>
  (uiSelectedProductSelector(state) === Product.Loan &&
    configApplicationFlowSelector(state) === LoanFlow.StepbystepApplication &&
    uiSelectedStepSelector(state)[Product.Loan] === LoanStep.Application) ||
  (uiSelectedProductSelector(state) === Product.LifeInsurance &&
    uiSelectedStepSelector(state)[Product.LifeInsurance] === LifeInsuranceStep.Application)

export const uiSelectedLoanApplicationStepSelector = (state: MultiProductState): ApplicationStep | undefined =>
  uiSelectedApplicationStepSelector(state)?.loan

export const uiShouldHaveTrustBadgesSelector = (state: MultiProductState): boolean => {
  const selectedProduct = uiSelectedProductSelector(state) ?? ''
  const selectedStep = uiSelectedStepSelector(state)[selectedProduct]
  return (
    selectedProduct === Product.LifeInsurance &&
    (selectedStep === LifeInsuranceStep.Results || selectedStep === LifeInsuranceStep.PersonalInfo)
  )
}

export const uiMaxWidthForStep = (state: MultiProductState): string => {
  const widthSmall = '750px'
  if (!configFixedWidthSelector(state)) {
    return 'none'
  }
  if (isStepByStepSelector(state)) {
    return widthSmall
  }
  const product = uiSelectedProductSelector(state) ?? ''
  const step = uiSelectedStepSelector(state)[product]
  switch (product) {
    case Product.CreditCard:
      return step === CreditCardStep.Intro ? widthSmall : 'none'
    case Product.LifeInsurance:
      if (step === LifeInsuranceStep.PersonalInfo) {
        return '1400px'
      }
      if (step === LifeInsuranceStep.Results || step === LifeInsuranceStep.RequestACall) {
        return 'none'
      }
      return widthSmall
    default:
      return 'none'
  }
}
